import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Field } from 'src/app/models/field/field.model';
import { CustomCodingFieldsManager } from 'src/app/models/review/custom-coding-fields-manager';
import { KeyHandlerDirective } from '../../../directives/key-handler.directive';
import { BuiltInFieldsService } from 'src/app/services/built-in-fields.service';
import { BuiltInFieldId } from 'src/app/models/built-in-field-id.enum';
import { BehaviorSubject, Observable } from 'rxjs';
import { cloneDeep } from 'lodash-es';
import { FieldType } from '@models/field/field-type.enum';
import { CodingField } from '@models/field/coding-field.model';

export interface Sort {
  fieldId: string;
  direction: 'ASC' | 'DESC';
}

export interface MultiSort {
  first: Sort;
  second: Sort;
  third: Sort;
}

@Component({
    selector: 'lex-select-sort',
    templateUrl: './select-sort.component.html',
    styleUrls: ['./select-sort.component.scss'],
    standalone: false
})
export class SelectSortComponent extends KeyHandlerDirective implements OnInit {

  public sortFields: string[][] = [];
  public ccfManager: CustomCodingFieldsManager;
  
  public sorts$ = new Observable<Sort[]>();
  private sortsSubject = new BehaviorSubject<Sort[]>([]);
  private readonly emptySort: Sort = {
    fieldId: '',
    direction: 'ASC'
  };
  private curSort: MultiSort = {
    first: cloneDeep(this.emptySort),
    second: cloneDeep(this.emptySort),
    third: cloneDeep(this.emptySort)
  };
  private isProduction: boolean = false;
  private parentsSubject = new BehaviorSubject<CodingField[]>([]);
  private childrenMapSubject = new BehaviorSubject<Map<string, CodingField[]>>(new Map());
  parents$: Observable<CodingField[]> = this.parentsSubject.asObservable();
  childrenMap$: Observable<Map<string, CodingField[]>> = this.childrenMapSubject.asObservable();

  constructor(
    private dialogRef: MatDialogRef<SelectSortComponent>,
    private builtInFieldsService: BuiltInFieldsService,
    @Inject(MAT_DIALOG_DATA) data,
  ) {
    super();
    const sorts: Sort[] = [];
    if (data.currentSort) {
      if (data.currentSort.first.fieldId !== '')
        sorts.push(data.currentSort.first);

      if (data.currentSort.second.fieldId !== '')
        sorts.push(data.currentSort.second);

      if (data.currentSort.third.fieldId !== '')
        sorts.push(data.currentSort.third);
    }
    else {
      sorts.push(cloneDeep(this.emptySort));
    }

    this.sortsSubject.next(sorts);
    this.sorts$ = this.sortsSubject.asObservable();
    
    this.sorts$.subscribe(s => {
      this.curSort.first = s[0] ?? cloneDeep(this.emptySort);
      this.curSort.second = s[1] ?? cloneDeep(this.emptySort);
      this.curSort.third = s[2] ?? cloneDeep(this.emptySort);
    });

    this.ccfManager = data.ccfManager;
    this.isProduction = data.isProductionSort ?? false;
  }

  public ngOnInit(): void {
    if (this.isProduction){
      this.builtInFieldsService.getProductionSortFields().subscribe(data => {
        this.setSortFields(data);
      });
      const textFields = this.ccfManager.getAllChildren().filter(f => f.type === FieldType.TextField).sort((a, b) => a.rank > b.rank ? 1 : -1);
      const parents: CodingField[] = [];
      const childMap = new Map<string, CodingField[]>();
      textFields.forEach(f => {
        const parent = this.ccfManager.getParent(f.parentFieldId);
        if (!childMap.has(f.parentFieldId) && parent) {
          childMap.set(f.parentFieldId, [f]);
          parents.push(parent);
        } else if (childMap.has(f.parentFieldId) && parent && f.fieldId !== parent.fieldId) {
          const children = childMap.get(parent.fieldId) ?? [];
          children.push(f);
          children.sort((a, b) => a.rank - b.rank);
          childMap.set(parent.fieldId, children);
        }
      });  
      this.parentsSubject.next(parents);   
      this.childrenMapSubject.next(childMap);
    }
    else {
      this.builtInFieldsService.getFields().subscribe(data => {
        this.setSortFields(data.value);
      });

      const allParents = this.ccfManager.getParents();
      this.parentsSubject.next(allParents);
      const childrenMap = this.ccfManager.getChildMapSorted(); 
      this.childrenMapSubject.next(childrenMap);
    } 
  }

  public ok() {
    if (this.curSort.first.fieldId || this.curSort.second.fieldId || this.curSort.third.fieldId) {
      this.dialogRef.close(this.curSort);
    } else {
      this.dialogRef.close();
    }
  }

  public addSortLevel() {
    const sorts = this.sortsSubject.value || [];
    if (sorts.length >= 3)
      return;
    if (sorts[sorts.length - 1].fieldId === '')
      return;

    this.sortsSubject.next([...sorts, cloneDeep(this.emptySort)]);
  }

  public removeSortLevel() {
    const sorts = this.sortsSubject.value || [];
    if (sorts.length <= 1)
      return;

    this.sortsSubject.next(sorts.slice(0, -1));
  }

  private setSortFields(fields: Field[]) {
    fields.forEach((val: Field) => {
      if (val.fieldId === BuiltInFieldId.Score) return;
      const displayTitle = (this.isProduction && val.fieldId.toLocaleLowerCase() === BuiltInFieldId.MasterDate) ? `${val.displayTitle} (Default)` : val.displayTitle;
      this.sortFields.push([val.fieldId, displayTitle]);
    });

    this.sortFields.sort((a, b) => a[1] > b[1] ? 1 : -1);
  }
}
